import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["poster"]
const _hoisted_2 = ["src"]

import { MediaAPI } from '@/api';
import MetaHandler from '@/classes/MetaHandler';
import { getSaveImageUrl } from '@/helpers';
import { WpMedia } from '@/interfaces/wp/WpMedia';
import {
  computed, inject, nextTick, onBeforeMount, onMounted, Ref, ref, watch,
} from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'LazyBackgroundMedia',
  props: {
    media: {},
    mediaId: {},
    mediaUrl: {},
    previewMediaUrl: {},
    backgroundPositionY: {},
    backgroundPositionX: {},
    mediaDesktop: {},
    mediaIdDesktop: {},
    mediaUrlDesktop: {},
    previewMediaUrlDesktop: {},
    backgroundPositionYDesktop: { default: 'inherit' },
    backgroundPositionXDesktop: { default: 'inherit' },
    icon: { type: Boolean },
    noRepeat: { type: Boolean },
    immediate: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const root = ref<InstanceType<typeof HTMLElement>>();

const isMobileBreakpoint = inject('isMobileBreakpoint') as Ref<boolean>;
const currentBreakpoints = inject('currentBreakpoints') as Ref<string[]>;
const src = ref<string>('none');
const fetchedMedia = ref<WpMedia>();
const fetchedMediaUrl = ref<string>();
const fetchedMediaDesktop = ref<WpMedia>();
const fetchedMediaUrlDesktop = ref<string>();
const backgroundMediaUrl = computed<string | undefined>(() => {
  const mediaUrl = isMobileBreakpoint.value ? (fetchedMediaUrl.value || fetchedMediaUrlDesktop.value) : (fetchedMediaUrlDesktop.value || fetchedMediaUrl.value);

  if (mediaUrl) return getSaveImageUrl(mediaUrl);

  const fetchedMediaToUse = isMobileBreakpoint.value ? (fetchedMedia.value || fetchedMediaDesktop.value) : (fetchedMediaDesktop.value || fetchedMedia.value);

  if (fetchedMediaToUse) {
    const mimes = fetchedMediaToUse?.attachmentMediaSizes?.full?.mimes;
    const mimeKeys = Object.keys(mimes);

    if (mimeKeys.length >= 1) {
      return getSaveImageUrl(mimes[mimeKeys[0]]);
    }
  }

  return undefined;
});
const supportsWebP = inject('supportsWebP') as boolean;
const mediaType = computed<'none' | 'image' | 'video'>(() => {
  if (src.value === 'none') return 'none';

  if (src.value.endsWith('.mpeg') || src.value.endsWith('.mp4') || src.value.endsWith('.webm')) {
    return 'video';
  }

  return 'image';
});

const alreadyFetched = ref(false);
const fetchMedia = () => {
  if (alreadyFetched.value) return;

  let desktopAlreadyFetched = false;

  if (props.media) {
    fetchedMedia.value = props.media;
  } else if (props.mediaUrl && props.mediaUrl !== 'bnVsbA==') {
    fetchedMediaUrl.value = getSaveImageUrl(props.mediaUrl);
  } else if (props.mediaId && props.mediaId !== '0') {
    const mediaIDs = [props.mediaId];
    if (props.mediaIdDesktop && props.mediaIdDesktop !== '0') {
      mediaIDs.push(props.mediaIdDesktop);
      desktopAlreadyFetched = true;
    }

    MediaAPI.get(mediaIDs as number[]).then((response) => {
      // eslint-disable-next-line eqeqeq
      fetchedMedia.value = response.find((m) => m.id == props.mediaId) as WpMedia;

      if (props.mediaIdDesktop && props.mediaIdDesktop !== '0') {
        // eslint-disable-next-line eqeqeq
        fetchedMediaDesktop.value = response.find((m) => m.id == props.mediaIdDesktop) as WpMedia;
      }
    });
  }

  /** Desktop */
  if (props.mediaDesktop) {
    fetchedMediaDesktop.value = props.mediaDesktop;
  } else if (props.mediaUrlDesktop && props.mediaUrlDesktop !== 'bnVsbA==') {
    fetchedMediaUrlDesktop.value = getSaveImageUrl(props.mediaUrlDesktop);
  } else if (props.mediaIdDesktop && props.mediaIdDesktop !== '0' && !desktopAlreadyFetched) {
    MediaAPI.get(props.mediaIdDesktop as number).then((response) => {
      fetchedMediaDesktop.value = response[0] as WpMedia;
    });
  }

  alreadyFetched.value = true;
};

const getMediaSourcesObject = (media: WpMedia | undefined): Array<{ width: number, url: string }> => {
  let ms: Array<{ width: number, url: string }> = [];

  if (media) {
    const msizes: Array<{ width: number, url: string }> = [];
    Object.entries(media?.attachmentMediaSizes || {}).forEach(([sizeKey, size]) => {
      if (sizeKey === 'thumbnail') return;

      let mimeTypes = Object.keys(size.mimes);

      if (mimeTypes.length === 0) return;

      let mimeToUse = mimeTypes[0];

      if (mimeTypes.includes('image/webp')) {
        if (!supportsWebP && mimeTypes.length > 1) {
          mimeTypes = mimeTypes.filter((mt) => mt !== 'image/webp');
          // eslint-disable-next-line prefer-destructuring
          mimeToUse = mimeTypes[0];
        } else {
          mimeToUse = 'image/webp';
        }
      }

      msizes.push({
        width: size.width,
        url: size.mimes[mimeToUse],
      });
    });

    ms = msizes.sort((sizeA, sizeB) => sizeA.width - sizeB.width);
  }

  return ms;
};

const mediaSources = computed<Array<{
  width: number
  url: string
}>>(() => {
  let mediaSizes = getMediaSourcesObject(fetchedMedia.value || fetchedMediaDesktop.value);

  if (!isMobileBreakpoint.value && fetchedMediaDesktop.value) {
    mediaSizes = getMediaSourcesObject(fetchedMediaDesktop.value);
  }

  return mediaSizes;
});

const setBackgroundImage = () => {
  src.value = 'none';

  nextTick(() => {
    if (root.value) {
      const possibleSources = Object.values([...(mediaSources.value || [])]).filter((msrc) => !!(root.value && msrc.width >= root.value.offsetWidth));

      if (possibleSources.length) {
        if (props.icon) {
          src.value = possibleSources[1]?.url || possibleSources[0]?.url;
        } else {
          src.value = possibleSources[0].url;
        }
      } else if (Object.values(mediaSources.value).length) {
        src.value = Object.values(mediaSources.value)[Object.values(mediaSources.value).length - 1].url
        || Object.values(mediaSources.value)[Object.values(mediaSources.value).length - 1].url;
      } else if (isMobileBreakpoint && (props.mediaUrl || props.mediaUrlDesktop)) {
        src.value = (props.mediaUrl || props.mediaUrlDesktop) || 'none';
      } else if (!isMobileBreakpoint && (props.mediaUrlDesktop || props.mediaUrl)) {
        src.value = (props.mediaUrlDesktop || props.mediaUrl) || 'none';
      }
    }

    src.value = getSaveImageUrl(src.value) as string;

    if (src.value !== 'none') {
      let mimeType: string | undefined;
      let asType: 'image' | 'font' | 'video' | 'style' | 'script' | 'audio' | undefined;

      if (src.value.endsWith('.webp')) {
        mimeType = 'image/webp';
        asType = 'image';
      } else if (src.value.endsWith('.avif')) {
        mimeType = 'video/avif';
        asType = 'image';
      } else if (src.value.endsWith('.png')) {
        mimeType = 'image/png';
        asType = 'image';
      } else if (src.value.endsWith('.jpg') || src.value.endsWith('jpeg')) {
        mimeType = 'image/jpeg';
        asType = 'image';
      } else if (src.value.endsWith('.gif')) {
        mimeType = 'video/gif';
        asType = 'image';
      } else if (src.value.endsWith('.webm')) {
        mimeType = 'video/webm';
        asType = 'video';
      } else if (src.value.endsWith('.mp4')) {
        mimeType = 'video/mp4';
        asType = 'video';
      } else if (src.value.endsWith('.mpeg')) {
        mimeType = 'video/mpeg';
        asType = 'video';
      }

      if (asType && mimeType) {
        MetaHandler.addPrefetch(src.value, asType, mimeType);
      }
    }
  });
};

watch(() => currentBreakpoints.value, setBackgroundImage, {
  deep: true,
  immediate: true,
});

watch(() => mediaSources.value, setBackgroundImage, {
  deep: true,
  immediate: true,
});

const isInViewport = ref(false);

const handleIntersect = (
  entries: IntersectionObserverEntry[],
  observer: IntersectionObserver,
): void => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      isInViewport.value = true;
      fetchMedia();
      setBackgroundImage();
      if (root.value) {
        observer.unobserve(root.value);
      }
    }
  });
};

const createObserver = (): void => {
  const observer = new IntersectionObserver(handleIntersect, {
    root: null,
    threshold: 0,
  });

  if (root.value) {
    observer.observe(root.value);
  }
};

const backgroundStyles = computed(() => {
  let styles = `background-image: url('${src.value}');`;
  let posX = props.backgroundPositionX;
  let posY = props.backgroundPositionY;

  if (!isMobileBreakpoint.value) {
    if (['same', 'inherit'].includes(props.backgroundPositionXDesktop)) {
      posX = props.backgroundPositionX;
    }
    if (['same', 'inherit'].includes(props.backgroundPositionYDesktop)) {
      posY = props.backgroundPositionY;
    }
  }

  if (props.noRepeat) {
    styles += 'background-repeat: no-repeat;';
  }

  if (['left', 'center', 'right'].indexOf(posX || 'inherit') >= 0) {
    styles += `background-position-x: ${posX};`;
  }

  if (['top', 'center', 'bottom'].indexOf(posY || 'inherit') >= 0) {
    styles += `background-position-y: ${posY};`;
  }

  return styles;
});

onBeforeMount(() => {
  if (props.immediate) {
    fetchMedia();
  }
});

onMounted(() => {
  if (window.IntersectionObserver) {
    createObserver();
  } else {
    isInViewport.value = true;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "io-lazy-background",
    ref_key: "root",
    ref: root
  }, [
    (isInViewport.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          (src.value !== 'none' && mediaType.value !== 'video' && !_ctx.icon)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "image",
                style: _normalizeStyle(backgroundStyles.value)
              }, [
                _renderSlot(_ctx.$slots, "default")
              ], 4))
            : _createCommentVNode("", true),
          (mediaType.value === 'video')
            ? (_openBlock(), _createElementBlock("video", {
                autoplay: true,
                loop: true,
                muted: true,
                playsinline: true,
                poster: _ctx.previewMediaUrl,
                preload: "metadata",
                key: backgroundMediaUrl.value
              }, [
                (backgroundMediaUrl.value)
                  ? (_openBlock(), _createElementBlock("source", {
                      key: 0,
                      src: backgroundMediaUrl.value,
                      type: "video/mp4"
                    }, null, 8, _hoisted_2))
                  : _createCommentVNode("", true)
              ], 8, _hoisted_1))
            : _createCommentVNode("", true),
          (_ctx.icon)
            ? (_openBlock(), _createElementBlock("div", {
                key: 2,
                class: "icon",
                style: _normalizeStyle(backgroundStyles.value)
              }, null, 4))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true)
  ], 512))
}
}

})